
import { defineComponent, PropType } from 'vue';
import globalConstants from '@/shared/globalConstants';
import apmTransactions from '@/libraries/apm/transactions';
import { ActionTypes } from '@/store/actionTypes';
import DriverFeedback from '@/types/DriverFeedback';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import { mapGetters } from 'vuex';
import { DateTime } from 'luxon';
import { isNullOrUndefined } from '@/shared/helpers';
import DriverFeedbackForm from './DriverFeedbackForm.vue';
import { FeedbackTypes } from '@/types/FeedbackTypes';
import locationReverseGeocode from '@/libraries/heremaps/reverseGeocode';
import FbaApiError from '@/types/FbaApiError';
import { MutationTypes } from '@/store/mutationTypes';
import { LocationSource } from '@/types/DriverMetrics';

export interface FeedbackDetails {
    trailerNumber: string | null;
    customerName: string | null;
    partyType: number | null | undefined;
    mcDotNumber: string | null;
    truckNumber: string | null;
}

export default defineComponent({
    data(): {
        isActive: boolean;
        selectedOption: null | number;
        trailerNumber: null | string;
    } {
        return {
            isActive: false,
            selectedOption: null,
            trailerNumber: null,
        };
    },
    components: {
        DriverFeedbackForm,
    },
    props: {
        trailers: {
            type: Array as PropType<NearbyTrailerResult[]>,
            default: () => [],
        },
        viewName: {
            type: String,
            default: '',
        },
    },
    methods: {
        toggleMenu(): void {
            this.isActive = !this.isActive;

            window.addEventListener('mouseup', (event) => {
                if (
                    this.$refs.feedbackMenu !== null &&
                    !(this.$refs.feedbackMenu as any).contains(event.target) &&
                    !(this.$refs.questionButton as any).contains(event.target)
                ) {
                    this.closeMenu();
                    window.removeEventListener('mouseup', () =>
                        this.closeMenu()
                    );
                }
            });
        },
        closeMenu(): void {
            this.isActive = false;
            this.resetFeedbackSelection();
        },
        resetFeedbackSelection(): void {
            this.selectedOption = null;
        },
        async sendFeedback(feedbackDetails: FeedbackDetails): Promise<void> {
            if (this.selectedOption === null) return;
            apmTransactions.instrumentDriverFeedbackSubmission(
                globalConstants.driverFeedback.options[this.selectedOption],
                this.viewName
            );
            let locationString = '';

            if (!isNullOrUndefined(this.currentLocation)) {
                locationString =
                    this.currentLocation.code +
                    '|' +
                    this.currentLocation.company +
                    ' - ' +
                    this.currentLocation.name;
            } else if (!isNullOrUndefined(feedbackDetails.customerName)) {
                locationString = feedbackDetails.customerName;
            } else {
                locationString =
                    this.truck.address.city + ', ' + this.truck.address.state;
            }

            const timestamp = DateTime.now().toISO();

            const resultNames: string[] = [];

            this.trailers.forEach((result) =>
                resultNames.push(
                    isNullOrUndefined(result.locationName) ||
                        this.viewName ===
                            globalConstants.viewNames.selectTrailer
                        ? result.trailers[0].id
                        : result.customerId + ' - ' + result.locationName
                )
            );

            const username = isNullOrUndefined(this.user)
                ? ''
                : this.user.username;

            let driverLocation =
                this.bestAvailableLocation.lat +
                ', ' +
                this.bestAvailableLocation.lng;

            const driverLocationResponse = await locationReverseGeocode(
                this.bestAvailableLocation
            );

            if (driverLocationResponse instanceof FbaApiError) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    driverLocationResponse as FbaApiError
                );
            } else {
                driverLocation =
                    driverLocationResponse.city +
                    ', ' +
                    driverLocationResponse.state;
            }

            let driverPhoneNumber = null;

            if (this.driver.phoneNumbers.length > 0)
                driverPhoneNumber = this.driver.phoneNumbers[0].number;

            const feedbackData = new DriverFeedback(
                globalConstants.driverFeedback.optionCodes[this.selectedOption],
                this.driver.id,
                username,
                locationString,
                resultNames,
                timestamp,
                feedbackDetails.trailerNumber,
                feedbackDetails.customerName,
                feedbackDetails.partyType,
                feedbackDetails.mcDotNumber,
                feedbackDetails.truckNumber,
                driverLocation,
                driverPhoneNumber,
                this.viewName,
                this.company,
                this.bestAvailableLocation.lat,
                this.bestAvailableLocation.lng,
                this.deviceInfo.isGeoLocationShared
                    ? LocationSource.DEVICE_GPS
                    : LocationSource.TRUCK,
                this.currentLocation?.code
            );

            await this.$store.dispatch(
                ActionTypes.SUBMIT_DRIVER_FEEDBACK,
                feedbackData
            );

            this.closeMenu();
        },
        openSurvey(): void {
            open('https://forms.gle/drHxHRbgAWH56Qht7', '_self');
        },
        onRowClick(feedbackOption: number) {
            this.selectedOption = feedbackOption;
        },
    },
    computed: {
        ...mapGetters({
            currentLocation: 'getCustomerAtCurrentLocation',
            driver: 'getDriver',
            truck: 'getTruck',
            user: 'getUser',
            company: 'getCompany',
            bestAvailableLocation: 'getPinnedBestAvailableLocation',
            deviceInfo: 'getDeviceInfo',
        }),
        feedbackOptions(): string[] {
            return globalConstants.driverFeedback.options;
        },
        isDamageOption() {
            return this.feedbackOptions.findIndex(
                (_, i) => i === FeedbackTypes.trailerDamaged
            );
        },
        isSwiftDriver() {
            return this.driver.company === globalConstants.companies.swift;
        },
    },
});
